import { ErrorCode } from "./codes.js";

/**
 * Base class for every expected failure in the system.
 *
 * A `DomainError` is RETURNED, not thrown — it is the `err` channel of an `AppResult`. It extends
 * `Error` so a stack trace is available when one genuinely escapes, not because it is meant to be
 * thrown. `try`/`catch` is a lint error in domain and application layers.
 *
 * `statusCode` lives here rather than in the HTTP layer so a context can state the meaning of its
 * own failure once. The mapper in apps/api still owns the final translation.
 */
export class DomainError extends Error {
  constructor(
    public readonly code: ErrorCode,
    message: string,
    public readonly statusCode: number,
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "DomainError";
    // Restores the prototype chain so `instanceof SubClass` works after transpilation.
    Object.setPrototypeOf(this, new.target.prototype);
  }
}

/** Unexpected failure. Reaching for this in a domain package is usually a modelling gap. */
export class SysInternalError extends DomainError {
  constructor(details?: Record<string, unknown>) {
    super(ErrorCode.SYS_INTERNAL, "An unexpected error occurred.", 500, details);
  }
}

/**
 * A throwing database call, wrapped at the adapter boundary.
 *
 * Adapters are the only place allowed to catch: they exist to turn a throwing dependency into a
 * Result so nothing above them has to.
 */
export class SysDatabaseError extends DomainError {
  constructor(details?: Record<string, unknown>) {
    super(ErrorCode.SYS_DATABASE, "A database error occurred.", 500, details);
  }
}

/** An external service was unreachable or failed in a way worth retrying. */
export class SysUnavailableError extends DomainError {
  constructor(details?: Record<string, unknown>) {
    super(ErrorCode.SYS_UNAVAILABLE, "A required service is unavailable.", 503, details);
  }
}

export function isDomainError(value: unknown): value is DomainError {
  return value instanceof DomainError;
}
